import { useEffect, useMemo, useState } from 'react';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { ExpandableSearch } from '@components/expandable-search';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconBuilder } from '@components/icons/builder';
import { IconInfoSimple } from '@components/icons/info-simple';
import { IconSettings } from '@components/icons/settings';
import { IconStatistics } from '@components/icons/statistics';
import { IconTraveller } from '@components/icons/traveller';
import { IconWorkspace } from '@components/icons/workspace';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { useTableRef } from '@components/table/table';
import { pxToRem } from '@utils/converters';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { useJourney, useLoadJourneyUserSettings } from 'api/journeys/hooks';
import { JourneyType, TravellerJourneyListItem } from 'api/types';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

import { JourneyMenu } from './common';
import { JourneyBreadcrumb } from './journey-breadcrumb';
import { useJourneyDetailsSidebar } from './journey-edit-sidebar';
import { JourneyFields } from './journey-fields';
import { JourneySettings } from './journey-settings';
import { OverviewTab } from './overview';
import { SelectionHeader } from './selection-header';
import { t } from './traveller-journey-list/locale';
import { TravellerJourneysTable } from './traveller-journey-list/traveller-journeys-table';

import { useCurrentWorkspace } from '../../helpers';
import { useCompanyTravellerToJourneySidebar } from '../travellers/company-traveller-to-journey';

const JourneyDetailActions = ({
  journey,
  onSearchChange,
  showDetailSidebar,
}: {
  journey: JourneyType;
  onSearchChange(search: string): void;
  showDetailSidebar: (props: { journey: JourneyType }) => void;
}) => {
  const navigate = useNavigate();
  const showAddCompanyTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const { canManageJourneyTravellers } = usePermissions();

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={onSearchChange} />
      {canManageJourneyTravellers && (
        <Button
          disabled={!journey.hasPublishedJourney}
          tooltip={!journey.hasPublishedJourney ? t('noPublishedJourneyTooltip') : undefined}
          variant="secondary"
          text={t('addTraveller')}
          iconLeft={<IconAddPlus />}
          onClick={() => showAddCompanyTravellerSidebar({ journeyShortId: journey.shortId })}
        />
      )}
      <Button
        variant="secondary"
        text={t('openJourneyBuilder')}
        iconLeft={<IconBuilder />}
        onClick={() =>
          navigate(
            generatePath(routePaths.editJourney, {
              workspaceId: journey.workspace,
              journeyId: journey.shortId,
              '*': '',
            })
          )
        }
      />
      <IconButton
        icon={<IconInfoSimple />}
        id="journey-details"
        variant="secondary-purple"
        onClick={() => showDetailSidebar({ journey })}
      />
      <JourneyMenu journey={journey} />
    </Stack>
  );
};

export const JourneyDetails = () => {
  const [searchStr, setSearchStr] = useState('');
  const { isEnabled: hasTimeSavedEnabled } = useFeature('FEATURE_TIME_SAVED');
  const params = useParams();
  const journeyId = params.journeyId ?? '';
  const journey = useJourney(journeyId);
  const [selectedTravellers, setSelectedTravellers] = useState<Array<TravellerJourneyListItem>>([]);
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const showDetailSidebar = useJourneyDetailsSidebar();
  const attributes: Array<AttributeType | TravellerAttributeTemplate> = useMemo(() => {
    const companyTravellerAttributes = companyTravellerAttributeTemplatesQuery.data;
    return [
      ...(companyTravellerAttributes ?? []).map((attribute) => ({
        ...attribute,
        isTravellerAttribute: true,
      })),
      ...(journey.data?.attributes ?? []),
    ];
  }, [companyTravellerAttributeTemplatesQuery.data, journey.data?.attributes]);

  const tableRef = useTableRef();
  const workspace = useCurrentWorkspace();
  const WorkspaceIcon = ICON_CHOOSER_ICONS[workspace.icon] || IconWorkspace;

  useEffect(() => {
    setSelectedTravellers([]);
    tableRef.current?.clearSelected?.();
  }, [journeyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const journeySettingsQuery = useLoadJourneyUserSettings(journeyId, {
    enabled: Boolean(journeyId),
  });

  if (journey.isLoading || journeySettingsQuery.isLoading) {
    return <Spinner />;
  }

  if (journey.isError || journeySettingsQuery.isError) {
    return <Navigate to={routePaths.travellersList} />;
  }

  const tabs = [
    {
      label: t('travellers'),
      section: 'travellers',
      icon: <IconTraveller />,
      content: (
        <TravellerJourneysTable
          key={journeyId}
          settings={journeySettingsQuery.data}
          searchStr={searchStr}
          setSelectedTravellers={setSelectedTravellers}
          journey={journey.data}
          tableRef={tableRef}
          attributes={attributes}
        />
      ),
    },
    {
      label: t('fields'),
      section: 'attributes',
      icon: <IconAttributeJourney />,
      content: <JourneyFields attributes={journey.data.attributes} journeyShortId={journeyId} />,
    },
    {
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
      content: <JourneySettings journeyTimezone={journey.data.timezone as string} />,
    },
    ...(hasTimeSavedEnabled
      ? [
          {
            label: t('overview'),
            section: 'overview',
            icon: <IconStatistics />,
            content: <OverviewTab journeyShortId={journeyId} />,
          },
        ]
      : []),
  ];

  return (
    <RouterTabsHeader
      actions={
        <JourneyDetailActions
          onSearchChange={setSearchStr}
          journey={journey.data}
          showDetailSidebar={showDetailSidebar}
        />
      }
      showSelectionHeader={selectedTravellers.length > 0}
      selectionHeader={
        <SelectionHeader
          clearSelectedTravellers={() => tableRef.current?.clearSelected()}
          selectedTravellers={selectedTravellers}
          journeyId={journeyId}
        />
      }
      breadcrumbs={
        <JourneyBreadcrumb
          journey={journey.data}
          workspaceId={journey.data.workspace}
          showEditSidebar={() => showDetailSidebar({ journey: journey.data })}
        />
      }
      tabs={tabs}
      backAction={
        <IconContainer
          hasBorder
          icon={<WorkspaceIcon color={workspace.color} />}
          variant={COLOR_TO_VARIANT[workspace.color]}
        />
      }
    />
  );
};

const IconContainer = styled(IconFancyContainer)({
  borderWidth: pxToRem(1),
});
